"use client";

import { appStore } from "@/app/store";
import { UseChatHelpers } from "@ai-sdk/react";
import { ChatMention, ChatMessageAnnotation, ChatModel } from "app-types/chat";
import {
  AudioWaveformIcon,
  BrainIcon,
  ChevronDown,
  CornerRightUp,
  Paperclip,
  Pause,
} from "lucide-react";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "ui/button";
import { notImplementedToast } from "ui/shared-toast";
import { useShallow } from "zustand/shallow";
import { SelectModel } from "./select-model";
import { ToolModeDropdown } from "./tool-mode-dropdown";

import type { Editor } from "@tiptap/core";
import { WorkflowSummary } from "app-types/workflow";
import { cn } from "lib/utils";
import { useTranslations } from "next-intl";
import { Tooltip, TooltipContent, TooltipTrigger } from "ui/tooltip";
import { ToolSelectDropdown } from "./tool-select-dropdown";
import { Shortcuts, isShortcutEvent } from "lib/keyboard-shortcuts";

interface PromptInputProps {
  placeholder?: string;
  setInput: (value: string) => void;
  input: string;
  onStop: () => void;
  append: UseChatHelpers["append"];
  toolDisabled?: boolean;
  isLoading?: boolean;
  model?: ChatModel;
  setModel?: (model: ChatModel) => void;
  voiceDisabled?: boolean;
  thinking?: boolean;
  onThinkingChange?: (thinking: boolean) => void;
}

const ChatMentionInput = dynamic(() => import("./chat-mention-input"), {
  ssr: false,
  loading() {
    return <div className="h-[2rem] w-full animate-pulse"></div>;
  },
});

export default function PromptInput({
  placeholder,
  append,
  model,
  setModel,
  input,
  setInput,
  onStop,
  isLoading,
  toolDisabled,
  voiceDisabled,
  thinking,
  onThinkingChange,
}: PromptInputProps) {
  const t = useTranslations("Chat");

  const [currentThreadId, currentProjectId, globalModel, appStoreMutate] =
    appStore(
      useShallow((state) => [
        state.currentThreadId,
        state.currentProjectId,
        state.chatModel,
        state.mutate,
      ]),
    );

  const chatModel = useMemo(() => {
    return model ?? globalModel;
  }, [model, globalModel]);

  const editorRef = useRef<Editor | null>(null);

  const setChatModel = useCallback(
    (model: ChatModel) => {
      if (setModel) {
        setModel(model);
      } else {
        appStoreMutate({ chatModel: model });
      }
    },
    [setModel, appStoreMutate],
  );

  const [toolMentionItems, setToolMentionItems] = useState<ChatMention[]>([]);

  const onSelectWorkflow = useCallback((workflow: WorkflowSummary) => {
    const workflowMention: ChatMention = {
      type: "workflow",
      workflowId: workflow.id,
      icon: workflow.icon,
      name: workflow.name,
      description: workflow.description,
    };
    editorRef.current
      ?.chain()

      .insertContent({
        type: "mention",
        attrs: {
          label: `${workflow.name} `,
          id: JSON.stringify(workflowMention),
        },
      })
      .focus()
      .run();
  }, []);

  const submit = () => {
    if (isLoading) return;
    const userMessage = input?.trim() || "";
    if (userMessage.length === 0) return;
    const annotations: ChatMessageAnnotation[] = [];
    if (toolMentionItems.length > 0) {
      annotations.push({
        mentions: toolMentionItems,
      });
    }
    setToolMentionItems([]);
    setInput("");
    append!({
      role: "user",
      content: "",
      annotations,
      parts: [
        {
          type: "text",
          text: userMessage,
        },
      ],
    });
  };

  // Handle thinking mode keyboard shortcut
  useEffect(() => {
    if (!onThinkingChange) {
      return; // No cleanup needed when onThinkingChange is not provided
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isShortcutEvent(e, Shortcuts.thinkingMode)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        onThinkingChange(!thinking);
        editorRef.current?.commands.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onThinkingChange, thinking]);

  return (
    <div className="max-w-3xl mx-auto fade-in animate-in">
      <div className="z-10 mx-auto w-full max-w-3xl relative">
        <fieldset className="flex w-full min-w-0 max-w-full flex-col px-2">
          <div className="rounded-4xl backdrop-blur-sm transition-all duration-200 bg-muted/60 relative flex w-full flex-col cursor-text z-10 items-stretch focus-within:bg-muted hover:bg-muted p-3">
            <div className="flex flex-col gap-3.5 px-1">
              <div className="relative min-h-[2rem]" data-tour="chat-input">
                <ChatMentionInput
                  input={input}
                  onChange={setInput}
                  onChangeMention={setToolMentionItems}
                  onEnter={submit}
                  placeholder={placeholder ?? t("placeholder")}
                  ref={editorRef}
                />
              </div>
              <div className="flex w-full items-center gap-[1px]  z-30">
                <Button
                  variant={"ghost"}
                  size={"sm"}
                  className="rounded-full hover:bg-input! p-2!"
                  onClick={notImplementedToast}
                >
                  <Paperclip />
                </Button>

                {!toolDisabled && (
                  <>
                    <ToolModeDropdown />
                    <ToolSelectDropdown
                      align="start"
                      side="top"
                      onSelectWorkflow={onSelectWorkflow}
                      mentions={toolMentionItems}
                    />
                  </>
                )}
                <div className="flex-1" />

                <SelectModel onSelect={setChatModel} defaultModel={chatModel}>
                  <Button
                    variant={"ghost"}
                    size={"sm"}
                    className="rounded-full data-[state=open]:bg-input! hover:bg-input! mr-1"
                  >
                    {chatModel?.model ?? (
                      <span className="text-muted-foreground">model</span>
                    )}
                    <ChevronDown className="size-3" />
                  </Button>
                </SelectModel>
                {onThinkingChange && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={"ghost"}
                        size={"sm"}
                        className={cn(
                          "rounded-full hover:bg-input! p-2! transition-all duration-200",
                          thinking
                            ? "bg-primary text-primary-foreground hover:bg-primary/90"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                        onClick={() => {
                          onThinkingChange(!thinking);
                          editorRef.current?.commands.focus();
                        }}
                      >
                        <BrainIcon size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {t("sequentialThinking")}
                      <span className="text-muted-foreground ml-2">
                        {Shortcuts.thinkingMode.shortcut.command ? "⌘" : "Ctrl"}+Shift+T
                      </span>
                    </TooltipContent>
                  </Tooltip>
                )}
                {!isLoading && !input.length && !voiceDisabled ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size={"sm"}
                        onClick={() => {
                          appStoreMutate((state) => ({
                            voiceChat: {
                              ...state.voiceChat,
                              isOpen: true,
                              threadId: currentThreadId ?? undefined,
                              projectId: currentProjectId ?? undefined,
                            },
                          }));
                        }}
                        className="rounded-full p-2!"
                      >
                        <AudioWaveformIcon size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("VoiceChat.title")}</TooltipContent>
                  </Tooltip>
                ) : (
                  <div
                    onClick={() => {
                      if (isLoading) {
                        onStop();
                      } else {
                        submit();
                      }
                    }}
                    className="fade-in animate-in cursor-pointer text-muted-foreground rounded-full p-2 bg-secondary hover:bg-accent-foreground hover:text-accent transition-all duration-200"
                  >
                    {isLoading ? (
                      <Pause
                        size={16}
                        className="fill-muted-foreground text-muted-foreground"
                      />
                    ) : (
                      <CornerRightUp size={16} />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </fieldset>
      </div>
    </div>
  );
}
